/**
 * The shiur time form (admin Times tab for shiurim) ↔ ShiurSession.
 * Pure; the engine that reads sessions is utils/shiurTimes.ts.
 */
import type {
  DayOfWeek,
  ShiurAudience,
  HoursDayType,
  MinyanTimeRule,
  ShiurAnchorPick,
  ShiurAnchorPrayer,
  ShiurSession,
} from '../types';
import { BETWEEN_MINCHA_MAARIV_DEFAULT_MINUTES } from '../config/categories';
import { HOURS_SPECIAL_DAY_TYPES, HoursDraftKind, HoursSeasonMode } from './hoursForm';
import { draftToTimeRule, TimeRuleDraft, timeRuleToDraft } from './timeRuleDraft';

/** Special days an owner can give a shiur time (no shiur on them otherwise). Purim too. */
export const SHIUR_SPECIAL_DAY_TYPES: HoursDayType[] = [...HOURS_SPECIAL_DAY_TYPES, 'purim'];

/** "At a time" (fixed or zman) or "by the shul's minyan" (shiurim in a shul only). */
export type ShiurStartMode = 'time' | 'minyan';

export const SHIUR_ANCHORS: MinyanTimeRule['anchor'][] = ['betweenMinchaMaariv', 'before', 'after'];
export const SHIUR_ANCHOR_PRAYERS: ShiurAnchorPrayer[] = ['shacharit', 'mincha', 'maariv'];
export const SHIUR_ANCHOR_PICKS: ShiurAnchorPick[] = ['nearestSunset', 'earliest', 'latest'];

export interface ShiurSessionDraft {
  kind: HoursDraftKind;
  daysOfWeek: DayOfWeek[];
  dayTypes: HoursDayType[];
  specificDate: string;
  /** One date only: "no shiur on this date". */
  cancelled: boolean;
  startMode: ShiurStartMode;
  start: TimeRuleDraft;
  anchor: MinyanTimeRule['anchor'];
  prayer: ShiurAnchorPrayer;
  pick: ShiurAnchorPick;
  offsetMinutes: number;
  hasEnd: boolean;
  end: TimeRuleDraft;
  noteHe: string;
  noteEn: string;
  season: HoursSeasonMode;
  validFrom: string;
  validTo: string;
}

export type ShiurSessionError = 'daysRequired' | 'dateRequired' | 'seasonInvalid' | 'minyanNeedsShul' | 'offsetInvalid';

/** A new time: Sunday–Thursday 20:30, or between Mincha and Maariv when the shiur is in a shul. */
export function emptyShiurDraft(inShul: boolean): ShiurSessionDraft {
  return {
    kind: 'days',
    daysOfWeek: [0, 1, 2, 3, 4],
    dayTypes: [],
    specificDate: '',
    cancelled: false,
    startMode: inShul ? 'minyan' : 'time',
    start: timeRuleToDraft(undefined, '20:30'),
    anchor: 'betweenMinchaMaariv',
    prayer: 'maariv',
    pick: 'nearestSunset',
    offsetMinutes: BETWEEN_MINCHA_MAARIV_DEFAULT_MINUTES,
    hasEnd: false,
    end: timeRuleToDraft(undefined, '21:30'),
    noteHe: '',
    noteEn: '',
    season: 'allYear',
    validFrom: '',
    validTo: '',
  };
}

export function sessionToDraft(session: ShiurSession): ShiurSessionDraft {
  const base = emptyShiurDraft(false);
  const minyan = session.start.type === 'minyan' ? session.start : undefined;
  return {
    ...base,
    kind: session.specificDate ? 'date' : 'days',
    daysOfWeek: [...(session.daysOfWeek ?? [])],
    dayTypes: (session.dayTypes ?? []).filter((d) => d !== 'weekday'),
    specificDate: session.specificDate ?? '',
    cancelled: session.cancelled === true,
    startMode: minyan ? 'minyan' : 'time',
    start: minyan ? base.start : timeRuleToDraft(session.start as Exclude<ShiurSession['start'], MinyanTimeRule>, '20:30'),
    anchor: minyan?.anchor ?? base.anchor,
    prayer: minyan?.prayer ?? base.prayer,
    pick: minyan?.pick ?? base.pick,
    offsetMinutes: minyan?.offsetMinutes ?? base.offsetMinutes,
    hasEnd: !!session.end,
    end: timeRuleToDraft(session.end, '21:30'),
    noteHe: session.noteHe ?? '',
    noteEn: session.noteEn ?? '',
    season: session.season ?? (session.validFrom || session.validTo ? 'dates' : 'allYear'),
    validFrom: session.validFrom ?? '',
    validTo: session.validTo ?? '',
  };
}

/** @param inShul the shiur is linked to a shul (needed for minyan-tied starts) */
export function checkShiurDraft(draft: ShiurSessionDraft, inShul: boolean): ShiurSessionError[] {
  const errors: ShiurSessionError[] = [];
  if (draft.kind === 'days' && draft.daysOfWeek.length === 0 && draft.dayTypes.length === 0) errors.push('daysRequired');
  if (draft.kind === 'date' && !draft.specificDate) errors.push('dateRequired');
  if (draft.season === 'dates' && draft.validFrom && draft.validTo && draft.validFrom > draft.validTo) errors.push('seasonInvalid');
  if (!draft.cancelled && draft.startMode === 'minyan') {
    if (!inShul) errors.push('minyanNeedsShul');
    if (!Number.isInteger(draft.offsetMinutes) || draft.offsetMinutes < 0 || draft.offsetMinutes > 240) errors.push('offsetInvalid');
  }
  return errors;
}

/** The session to store. Only the fields for the chosen kind are kept. */
export function draftToSession(draft: ShiurSessionDraft, id: string): ShiurSession {
  const start: ShiurSession['start'] =
    draft.startMode === 'minyan'
      ? {
          type: 'minyan',
          anchor: draft.anchor,
          ...(draft.anchor === 'betweenMinchaMaariv' ? {} : { prayer: draft.prayer }),
          pick: draft.pick,
          offsetMinutes: draft.offsetMinutes,
        }
      : draftToTimeRule(draft.start);
  const session: ShiurSession = {
    id,
    daysOfWeek: draft.kind === 'days' ? [...draft.daysOfWeek].sort((a, b) => a - b) : [],
    dayTypes: draft.kind === 'days' ? [...draft.dayTypes] : [],
    start,
  };
  if (draft.kind === 'date') {
    session.specificDate = draft.specificDate;
    if (draft.cancelled) session.cancelled = true;
  }
  // Between Mincha and Maariv ends at Maariv by itself.
  const endsByItself = draft.startMode === 'minyan' && draft.anchor === 'betweenMinchaMaariv';
  if (draft.hasEnd && !endsByItself && !session.cancelled) session.end = draftToTimeRule(draft.end);
  if (draft.noteHe.trim()) session.noteHe = draft.noteHe.trim();
  if (draft.noteEn.trim()) session.noteEn = draft.noteEn.trim();
  if (draft.kind !== 'date') {
    if (draft.season === 'winterClock' || draft.season === 'summerClock') session.season = draft.season;
    if (draft.season === 'dates') {
      if (draft.validFrom) session.validFrom = draft.validFrom;
      if (draft.validTo) session.validTo = draft.validTo;
    }
  }
  return session;
}

/** "Everyone" and the specific groups don't mix: picking one clears the other. */
export function nextAudience(prev: ShiurAudience[], next: ShiurAudience[]): ShiurAudience[] {
  const added = next.find((a) => !prev.includes(a));
  if (added === 'everyone') return ['everyone'];
  if (added) return next.filter((a) => a !== 'everyone');
  return next;
}
